/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Lookup, LookupValue } from "../../api/types/types";

/**
 * Shared utilities for campaign response lookup operations
 * Used by both campaign-response-type-lookup and campaign-response-reason-lookup
 */

/**
 * Parses a response code value to a number, handling string conversion and validation
 * @param responseCodeValue - The response code value (number, string, null, or undefined)
 * @returns The parsed number, or null if invalid
 */
export const parseResponseCodeValue = (
  responseCodeValue: number | string | null | undefined
): number | null => {
  if (responseCodeValue === null || responseCodeValue === undefined) {
    return null;
  }

  const codeValue =
    typeof responseCodeValue === "string" ? parseInt(responseCodeValue, 10) : responseCodeValue;

  if (isNaN(codeValue)) {
    return null;
  }

  return codeValue;
};

/**
 * Parses a value to a number, handling string conversion and validation
 * @param value - The value to parse (number, string, null, or undefined)
 * @returns The parsed number, or null if invalid
 */
export const parseValue = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsedValue = typeof value === "string" ? parseInt(value, 10) : value;

  if (isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
};

/**
 * Parses a value to a number, handling string conversion and validation
 * Returns undefined instead of null for consistency with optional number types
 * @param value - The value to parse (number, string, null, or undefined)
 * @returns The parsed number, or undefined if invalid
 */
export const parseValueToOptionalNumber = (
  value: number | string | null | undefined
): number | undefined => {
  const parsed = parseValue(value);
  return parsed === null ? undefined : parsed;
};

/**
 * Gets values from a lookup for a given response code value based on parentValues
 * @param lookup - The lookup data
 * @param responseCodeValue - The response code value (1, 2, 3, or 4)
 * @returns Array of values for that response code, or empty array if not found
 */
export const getValuesForResponseCode = (
  lookup: Lookup | undefined,
  responseCodeValue: number | string | null | undefined
): number[] => {
  if (!lookup?.values || responseCodeValue === null || responseCodeValue === undefined) {
    return [];
  }

  const codeValue = parseResponseCodeValue(responseCodeValue);
  if (codeValue === null) {
    return [];
  }

  return lookup.values
    .filter((item) => {
      // If parentValues is not defined, exclude the item
      if (!item.parentValues?.length) {
        return false;
      }
      return item.parentValues.includes(codeValue);
    })
    .map((item: LookupValue) => item.value)
    .filter((value): value is number => value !== undefined);
};

/**
 * Checks if a value is valid for a given response code value based on parentValues
 * @param lookup - The lookup data
 * @param value - The value to check
 * @param responseCodeValue - The response code value (1, 2, 3, or 4)
 * @returns True if the value is valid for the response code, false otherwise
 */
export const isValueValidForResponseCode = (
  lookup: Lookup | undefined,
  value: number | string | null | undefined,
  responseCodeValue: number | string | null | undefined
): boolean => {
  if (!lookup?.values || value == null || responseCodeValue == null) {
    return false;
  }

  const parsedValue = parseValue(value);
  const parsedCode = parseResponseCodeValue(responseCodeValue);

  if (parsedValue === null || parsedCode === null) {
    return false;
  }

  const lookupItem = lookup.values.find((item) => item.value === parsedValue);
  if (!lookupItem || !lookupItem.parentValues) {
    return false;
  }

  return lookupItem.parentValues.includes(parsedCode);
};

/**
 * Filters a lookup based on the selected response code
 * @param lookup - The lookup data to filter
 * @param responseCodeValue - The selected response code value
 * @returns Object containing filtered lookup and whether values are available
 */
export const getFilteredLookupForResponseCode = (
  lookup: Lookup | undefined,
  responseCodeValue: string | null | undefined
): { lookup: Lookup | undefined; hasValues: boolean } => {
  if (!lookup || !responseCodeValue) {
    return { lookup, hasValues: false };
  }

  const validValues = getValuesForResponseCode(lookup, responseCodeValue);
  if (validValues.length === 0) {
    return { lookup, hasValues: false };
  }

  const filteredValues = lookup.values.filter((item) => {
    const itemValue = item.value;
    return itemValue !== undefined && validValues.includes(itemValue);
  });

  return {
    lookup: {
      ...lookup,
      values: filteredValues,
    },
    hasValues: filteredValues.length > 0,
  };
};
